/**
 * Stage 6: raw RoomObjects → free "object" elements.
 *
 * A rotation near a 90° multiple snaps onto it (odd quarter-turns swap length
 * and width); any other is kept exactly, in the element's `rotation`.
 * RoomPlan's "storage" is both freestanding furniture (dressers,
 * shelves) and built-in cabinetry, so it maps to furniture unless it sits in
 * a cabinet run: see `markCabinetry`.
 */

import { makeCorner } from '../edits';
import { freeElementRing, normalizeDegrees } from '../geometry';
import { FloorPlanFreeElement, freeElementBase, ObjectSubtype, Point, RoomObject } from '../schemas';
import { ConversionOptions, ConversionReport, FT_PER_M } from './conversionTypes';
import { toFeetPoint } from './normalizeWalls';

const CATEGORY_MAP: Record<string, { subtype?: ObjectSubtype; notBuiltIn?: boolean }> = {
  storage: { subtype: 'furniture', notBuiltIn: true },
  bed: { subtype: 'furniture', notBuiltIn: true },
  chair: { subtype: 'furniture', notBuiltIn: true },
  sofa: { subtype: 'furniture', notBuiltIn: true },
  table: { subtype: 'furniture', notBuiltIn: true },
  television: { subtype: 'electronics', notBuiltIn: true },
  refrigerator: { subtype: 'appliance', notBuiltIn: true },
  stove: { subtype: 'appliance', notBuiltIn: true },
  oven: { subtype: 'appliance', notBuiltIn: true },
  dishwasher: { subtype: 'appliance', notBuiltIn: true },
  washerDryer: { subtype: 'appliance', notBuiltIn: true },
  bathtub: { subtype: 'bathtub' },
  toilet: { subtype: 'toilet' },
  sink: { subtype: 'sink' },
  fireplace: {},
  stairs: {},
};

/** Fixtures that are always set into cabinetry, so storage beside them is cabinets. */
const CABINET_ANCHORS = new Set(['sink', 'stove', 'oven', 'dishwasher']);
/** Storage this close (ft) to an anchor, or to cabinetry already found, is part of the same run. */
const CABINET_RUN_GAP = 0.5;
/** Floor cabinetry this tall (ft) or shorter is base cabinets under a counter; taller is a pantry or tall cabinet. */
const BASE_CABINET_MAX_HEIGHT = 4;

const bounds = (element: FloorPlanFreeElement) => {
  const ring = freeElementRing(element);
  const xs = ring.map(p => p.x), ys = ring.map(p => p.y);
  return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
};
const near = (a: FloorPlanFreeElement, b: FloorPlanFreeElement) => {
  const p = bounds(a), q = bounds(b);
  return p.minX - CABINET_RUN_GAP <= q.maxX && q.minX - CABINET_RUN_GAP <= p.maxX && p.minY - CABINET_RUN_GAP <= q.maxY && q.minY - CABINET_RUN_GAP <= p.maxY;
};

/**
 * Storage that runs up to a sink, stove, oven or dishwasher, directly or
 * through more such storage, is built-in cabinetry. Floor cabinets up to
 * counter height become counters (cabinets with a counter on top); wall
 * cabinets and tall cabinets become built-in cabinets.
 */
export function markCabinetry(elements: FloorPlanFreeElement[]): FloorPlanFreeElement[] {
  const inRun = new Set(elements.filter(element => CABINET_ANCHORS.has(element.name ?? '')));
  const storage = elements.filter(element => element.name === 'storage');
  for (let grew = true; grew;) {
    grew = false;
    for (const element of storage) {
      if (inRun.has(element) || ![...inRun].some(member => near(element, member))) continue;
      inRun.add(element);
      grew = true;
    }
  }
  return elements.map(element => {
    if (element.name !== 'storage' || !inRun.has(element)) return element;
    const { subtype: _subtype, notBuiltIn: _notBuiltIn, customAreaType: _customAreaType, ...box } = element as Extract<
      FloorPlanFreeElement,
      { customAreaType: 'object' }
    >;
    const floorStanding = freeElementBase(element) <= CABINET_RUN_GAP;
    return floorStanding && (element.height ?? 0) <= BASE_CABINET_MAX_HEIGHT
      ? { ...box, customAreaType: 'counter' as const, name: 'cabinets' }
      : { ...box, customAreaType: 'object' as const, subtype: 'cabinets' as const, name: 'cabinets' };
  });
}

/**
 * An object's footprint in room axes (ft). Near a 90° multiple it snaps onto
 * it: odd quarter turns swap the extents, and it has no rotation. Otherwise
 * the footprint stays length × width in its own axes and `rotation` carries
 * the angle, negated, because the raw frame is y-up and room coordinates are
 * y-down.
 */
function objectFootprint(o: RoomObject, snapDegrees: number): { extentX: number; extentY: number; rotation?: number } {
  const quarterTurns = Math.round(o.rotation / (Math.PI / 2));
  const residualDeg = Math.abs(o.rotation - quarterTurns * (Math.PI / 2)) * (180 / Math.PI);
  const snap = residualDeg <= snapDegrees;
  const swap = snap && ((quarterTurns % 2) + 2) % 2 === 1;
  return {
    extentX: (swap ? o.width : o.length) * FT_PER_M,
    extentY: (swap ? o.length : o.width) * FT_PER_M,
    rotation: snap ? undefined : normalizeDegrees(-o.rotation * (180 / Math.PI)) || undefined,
  };
}

/** Report what an object of an unknown category, or stairs, loses in conversion. */
function reportObjectCategory(o: RoomObject, report: ConversionReport): void {
  if (!(o.category in CATEGORY_MAP)) {
    report.warnings.push(`Object ${o.id}: unknown category "${o.category}", kept with no subtype`);
  }
  if (o.category === 'stairs') {
    report.warnings.push(`Object ${o.id}: stairs kept as a plain object (final stairs handling TBD)`);
  }
}

export function convertObjects(
  objects: RoomObject[],
  toRoom: (p: Point) => Point,
  opts: ConversionOptions,
  report: ConversionReport,
): FloorPlanFreeElement[] {
  return markCabinetry(objects.map(o => {
    const { extentX, extentY, rotation } = objectFootprint(o, opts.objectRotationSnapDeg);
    const center = toRoom(toFeetPoint(o.position));
    const baseFt = o.baseHeight * FT_PER_M;
    const mapping = CATEGORY_MAP[o.category] ?? {};
    reportObjectCategory(o, report);
    return {
      customAreaType: 'object' as const,
      name: o.category,
      x: center.x - extentX / 2,
      y: center.y - extentY / 2,
      width: extentX,
      length: extentY,
      height: o.height * FT_PER_M,
      base: baseFt > 0.05 ? baseFt : undefined,
      rotation,
      subtype: mapping.subtype,
      notBuiltIn: mapping.notBuiltIn,
      backLeftCorner: makeCorner(),
      backRightCorner: makeCorner(),
      frontRightCorner: makeCorner(),
      frontLeftCorner: makeCorner(),
    };
  }));
}
